import { useQuery } from '@tanstack/react-query'
import type {
  Overview,
  JournalDay,
  SpeciesDetail,
  Stats,
  Directory,
  Sort,
  Scope,
  Account,
  AdminHealth,
  About,
  Bibliography,
  StationPage,
  LoreLookup,
  NotesIndex,
  Note,
} from './types'

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`${res.status} ${url}`)
  return res.json() as Promise<T>
}

// The About tab's content — story/tech prose, source repos, support link. Static per deploy
// (profile files + config), so it's cached long and never refetched on its own.
export const useAbout = () =>
  useQuery<About>({
    queryKey: ['about'],
    queryFn: () => fetchJson('/api/about'),
    staleTime: Infinity,
  })

// The Sources tab's content — the station's full bibliography, bilingual markdown. A separate
// resource from About, same static-per-deploy cache.
export const useBibliography = () =>
  useQuery<Bibliography>({
    queryKey: ['bibliography'],
    queryFn: () => fetchJson('/api/bibliography'),
    staleTime: Infinity,
  })

// A standing prose page — 'privacy', 'letter' or 'tech'. Same static-per-deploy cache as About and
// Sources; the slug is the query key so the two never share a cache entry.
export const usePage = (slug: 'privacy' | 'letter' | 'tech') =>
  useQuery<StationPage>({
    queryKey: ['page', slug],
    queryFn: () => fetchJson(`/api/pages/${slug}`),
    staleTime: Infinity,
  })

// Notes from the station — the index, then one note. Two queries rather than one payload with
// every body in it: the index is a list somebody scans and the body is what they came for, and a
// station that has been writing for a year should not ship a year of prose to render nine titles.
//
// A minute of staleness matches the endpoint's own cache header. Anything longer and a note the
// keeper has just published stays invisible in an open tab.
export const useNotes = () =>
  useQuery<NotesIndex>({
    queryKey: ['notes'],
    queryFn: () => fetchJson('/api/posts'),
    staleTime: 60_000,
  })

// One note by slug. `enabled` keeps the index view from firing a request for a note nobody has
// opened yet — the hook is called unconditionally (rules of hooks) and simply idles at slug null.
export const useNote = (slug: string | null) =>
  useQuery<Note>({
    queryKey: ['note', slug],
    queryFn: () => fetchJson(`/api/posts/${slug}`),
    enabled: slug != null,
    staleTime: 60_000,
  })

export const useOverview = (h?: number) =>
  useQuery<Overview>({
    queryKey: ['overview', h ?? 24],
    queryFn: () => fetchJson(`/api/overview${h ? `?h=${h}` : ''}`),
  })

// The Journal: a completed day's frozen entry. `date` null → yesterday (the default).
export const useJournal = (date: string | null) =>
  useQuery<JournalDay>({
    queryKey: ['journal', date ?? 'yesterday'],
    queryFn: () => fetchJson(`/api/journal${date ? `?date=${date}` : ''}`),
  })

export const useStats = (h?: number) =>
  useQuery<Stats>({
    queryKey: ['stats', h ?? 24],
    queryFn: () => fetchJson(`/api/stats${h ? `?h=${h}` : ''}`),
  })

export const useDirectory = (sort: Sort, scope: Scope) =>
  useQuery<Directory>({
    queryKey: ['directory', sort, scope],
    queryFn: () => fetchJson(`/api/directory?sort=${sort}&scope=${scope}`),
  })

export const useSpecies = (sci: string | null) =>
  useQuery<SpeciesDetail>({
    queryKey: ['species', sci],
    queryFn: () => fetchJson(`/api/species/${encodeURIComponent(sci as string)}`),
    enabled: !!sci,
  })

// The species card's on-demand folklore look-up, followed to its end. The click POSTs to start
// a run (SpeciesModal); the run itself is a background job — minutes of Claude tool-use, far
// longer than CloudFront will hold a request open — so the card can't wait on one reply. It
// polls this instead, for as long as the server says a run is going. `enabled` is the card's
// "I have asked": before that there is nothing to poll. Session-authed, so it lives off /api.
export const LORE_LOOKUP_KEY = (sci: string | null) => ['lore-lookup', sci]

export const useLoreLookup = (sci: string | null, enabled: boolean) =>
  useQuery<LoreLookup>({
    queryKey: LORE_LOOKUP_KEY(sci),
    queryFn: () => fetchJson(`/species/${encodeURIComponent(sci as string)}/enrichment`),
    enabled: enabled && !!sci,
    // Stop the moment the run lands or the server says it found none. The server owns the
    // deadline (LoreLookup::STALE_AFTER), so a job that dies mid-run goes stale THERE and this
    // stops with it, rather than polling a bird that will never answer.
    refetchInterval: (query) => (query.state.data?.status === 'working' ? 5_000 : false),
    // The station is often a box on the LAN, where the browser's online heuristics are
    // unreliable and would pause the poll silently — same reason as useHealth below.
    networkMode: 'always',
  })

// The account panel's data — session-authed, so it lives off /api. Fetched only while
// the panel is open (`enabled`); the follow list itself comes from the FollowProvider.
export const useAccount = (enabled: boolean) =>
  useQuery<Account>({
    queryKey: ['account'],
    queryFn: () => fetchJson('/account'),
    enabled,
  })

// The admin health snapshot — session + admin-gated (403 off it). Polls while open so the
// listening dot and figures stay live; admin mutations invalidate ['health'] to refresh.
//
// networkMode 'always': by default react-query PAUSES a failed fetch when it believes the
// browser is offline, leaving the query pending-but-not-fetching with no error — the console
// then wedges (blank, and `refetch` won't fire) until a full reload. The station is often a
// box on the local network, where those online heuristics are unreliable, so always attempt
// the request and let a real failure surface as a real error.
export const useHealth = (enabled: boolean) =>
  useQuery<AdminHealth>({
    queryKey: ['health'],
    queryFn: () => fetchJson('/admin'),
    enabled,
    refetchInterval: 60_000,
    networkMode: 'always',
  })
